import { ReactNode } from "react";
import { useStoreProps } from "./single";
import { Form } from "react-bootstrap";
import { get, startCase } from "lodash";
import { ColorPicker, UnitPicker } from "../create";
import { ErrorOutline } from "@mui/icons-material";
import { ColorRangeEditor, ColorStop } from "../shared/ColorRangeEditor";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

type ChartPanel = {
    [key: string]: any;
};

// === Gauge Color Range Editor ===
const DEFAULT_GAUGE_RANGE_COLORS = ["#59BCFB", "#FFF157", "#FFA200", "#FF0000"];
const getRangeOffsets = (range: number) => {
    if (range === 2) return [50, 100];
    if (range === 3) return [33, 66, 100];
    return [25, 50, 75, 100];
};
const toDecimalOffset = (offset: number) => Number((offset / 100).toFixed(4));
const toPercentOffset = (offset: number) => offset;
const getRangeOffsetsAsDecimal = (range: number) =>
    getRangeOffsets(range).map(toDecimalOffset);
const getRangeOffsetsAsPercent = (range: number) =>
    getRangeOffsets(range).map(toPercentOffset);
const isDecimalOffsetScale = (colors: ColorStop[]) => {
    return (
        colors.length > 0 &&
        Math.max(...colors.map(([offset]) => Number(offset) || 0)) <= 1
    );
};
const buildRangeColors = (colors: ColorStop[], range: number): ColorStop[] => {
    const offsets = isDecimalOffsetScale(colors)
        ? getRangeOffsetsAsDecimal(range)
        : getRangeOffsetsAsPercent(range);

    return offsets.map((offset, index) => {
        const existingColor = colors[index]?.[1];
        const fallbackColor = DEFAULT_GAUGE_RANGE_COLORS[index];
        return [offset, existingColor ?? fallbackColor] as ColorStop;
    });
};
// === Gauge Color Range Editor End ===

export enum WidgetUpdateTemplateType {
    SENSOR,
    SENSOR_CARD,
    GAUGE,
    GAUGE_COLORS,
    ACTUATOR,
    GATEWAY,
    CARD_GATEWAY,
    OTHER,
    INDIVIDUAL_HEATMAP,
    SINGLE_CONSUMPTION,
    STORAGE,
    EVENT,
    NOTIFIER,
    SUBSCRIPTION,
    PSL, // Consolidated PSL widget (Room/Zone/Group determined by attributes[0].name)
}

export type UpdateWidgetProp = Partial<{
    [key in WidgetUpdateTemplateType]: {
        key: string;
        label: string | ((data: ChartPanel) => string);
        render?: (data: ChartPanel, store: useStoreProps) => ReactNode | string;
    }[];
}>;

const UpdateSingleChartTemplateRenderForName = (
    data: ChartPanel,
    store: useStoreProps
) => (
    <Form.Control
        style={{ width: "100%", maxWidth: 300 }}
        type="text"
        className="single-update-form-control"
        placeholder="Enter widget name"
        defaultValue={get(data, "name")}
        onChange={(e) => store.updatePanel("name", e.target.value)}
    />
);

const defaultDevicename = "Device information not found";
const SensorName = (data: ChartPanel, store: useStoreProps) => {
    const deviceName = store.getDeviceInfo(data)?.sensorName;
    const defaultName = defaultDevicename;

    return (
        <span
            className={`d-flex ${deviceName ? "mute" : "text-secondary-red-1"}`}
        >
            {!deviceName && <ErrorOutline className="mr-2" />}
            <span>{deviceName || defaultName}</span>
        </span>
    );
};

const GatewayName = (data: ChartPanel, store: useStoreProps) => {
    const gatewayId = get(data, "attributes[0].gateway_id", "");
    const gateway = store.allRegistry.find(
        ({ gateway_id }: { gateway_id: string }) => gateway_id === gatewayId
    );
    const gatewayName = get(gateway, "name", "");
    const defaultName = "Gateway information not found";

    return (
        <span
            className={`d-flex ${gatewayName ? "mute" : "text-secondary-red-1"
                }`}
        >
            {!gatewayName && <ErrorOutline className="mr-2" />}
            <span>{gatewayName || defaultName}</span>
        </span>
    );
};

const defaultRGZName = "Device information not found";
const RGZName = (data: ChartPanel, store: useStoreProps) => {
    const rgzName = store.getDeviceInfo(data)?.deviceName;
    const defaultName = defaultRGZName;
    return (
        <span className={`d-flex ${rgzName ? "mute" : "text-secondary-red-1"}`}>
            {!rgzName && <ErrorOutline className="mr-2" />}
            <span>{rgzName || defaultName}</span>
        </span>
    );
};

// Helper function to get PSL type label from panel attributes (Room/Zone/Group)
const getPSLTypeLabel = (data: ChartPanel): string => {
    const attributeName = get(data, "attributes[0].name", "").toLowerCase();

    if (attributeName.includes("room")) {
        return "Room";
    } else if (attributeName.includes("zone")) {
        return "Zone";
    } else if (attributeName.includes("group")) {
        return "Group";
    }

    return "Device"; // Fallback
};

// PSL template configuration (Room/Zone/Group determined dynamically from attributes[0].name)
const createPSLTemplate = () => [
    {
        key: "name",
        label: "Widget Name",
        render: UpdateSingleChartTemplateRenderForName,
    },
    {
        key: "device",
        label: (data: ChartPanel) => getPSLTypeLabel(data),
        render: RGZName,
    },
    {
        key: "gateway",
        label: "Network Name",
        render: GatewayName,
    },
    {
        key: "gatewayId",
        label: "Network Id",
        render: (data: ChartPanel) => (
            <span className="mute">
                {get(data, "attributes[0].gateway_id", "-")}
            </span>
        ),
    },
    {
        key: "color",
        label: "Color",
        render: (data: ChartPanel, store: useStoreProps) => (
            <ColorPicker
                defaultValue={get(data, "attributes[0].color")}
                onChange={(e, value) =>
                    store.updatePanel("attributes[0].color", value)
                }
                className="chart-col"
            />
        ),
    },
];

export const UpdateWidgetTemplates: UpdateWidgetProp = {
    [WidgetUpdateTemplateType.SENSOR]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "sensorName",
            label: "Sensor",
            render: SensorName,
        },
        {
            key: "type",
            label: "Type",
            render: (data, store) => (
                <span className="mute">
                    {get(data, "attributes[0].name", "-")}
                </span>
            ),
        },
        {
            key: "gateway",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "ldsu",
            label: LDSU_UUID_LABEL,
            render: (data) => {
                const ldsuId = get(data, "attributes[0].device_id", "");

                return (
                    <span
                        className={`d-flex ${ldsuId ? "mute" : "text-secondary-red-1"
                            }`}
                    >
                        {!ldsuId && (
                            <span className="material-icons mr-2">
                                error_outline
                            </span>
                        )}
                        <span>{ldsuId ?? "LDSU information not found"}</span>
                    </span>
                );
            },
        },
        {
            key: "unit",
            label: "Unit",
            render: (data, store) => {
                const deviceInfo = store.getDeviceInfo(data);

                return (
                    <UnitPicker
                        defaultValue={get(data, "attributes[0].unit")}
                        data={
                            deviceInfo?.units ||
                            deviceInfo?.unit
                                ?.split(",")
                                .map((item: string) => item.trim())
                        }
                        onChange={(e, value) =>
                            store.updatePanel("attributes[0].unit", value)
                        }
                        className="chart-col"
                    />
                );
            },
        },
        {
            key: "color",
            label: "Color",
            render: (data, store) => (
                <ColorPicker
                    defaultValue={get(data, "attributes[0].color")}
                    onChange={(e, value) =>
                        store.updatePanel("attributes[0].color", value)
                    }
                    className="chart-col"
                />
            ),
        },
        {
            key: "show_alert",
            label: "Show alert lines",
            render: (data, store) => {
                const deviceInfo = store.getDeviceInfo(data);
                const alertAvailable = get(
                    deviceInfo,
                    "alert_available",
                    false
                );
                return (
                    <div className="single-edit-switch">
                        <Form.Check
                            type="switch"
                            id={`switch-alert-${get(
                                deviceInfo,
                                "device_id",
                                ""
                            )}`}
                            label=""
                            checked={
                                alertAvailable
                                    ? Boolean(
                                        get(data, "attributes[0].show_alert")
                                    )
                                    : false
                            }
                            disabled={!alertAvailable}
                            onClick={(event) => {
                                store.updatePanel(
                                    "attributes[0].show_alert",
                                    get(event, "target.checked")
                                );
                            }}
                        />
                    </div>
                );
            },
        },
    ],
    [WidgetUpdateTemplateType.SENSOR_CARD]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "sensorName",
            label: "Sensor",
            render: SensorName,
        },
        {
            key: "type",
            label: "Type",
            render: (data, store) => (
                <span className="mute">
                    {get(data, "attributes[0].name", "-")}
                </span>
            ),
        },
        {
            key: "gateway",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "ldsu",
            label: LDSU_UUID_LABEL,
            render: (data) => {
                const ldsuId = get(data, "attributes[0].device_id", "");

                return (
                    <span
                        className={`d-flex ${ldsuId ? "mute" : "text-secondary-red-1"
                            }`}
                    >
                        {!ldsuId && (
                            <ErrorOutline className="mr-2" color="error" />
                        )}
                        <span>{ldsuId ?? "LDSU information not found"}</span>
                    </span>
                );
            },
        },
    ],
    [WidgetUpdateTemplateType.GAUGE]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "sensorName",
            label: "Sensor",
            render: SensorName,
        },
        {
            key: "type",
            label: "Type",
            render: (data, store) => (
                <span className="mute">
                    {get(data, "attributes[0].name", "-")}
                </span>
            ),
        },
        {
            key: "gateway",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "ldsu",
            label: LDSU_UUID_LABEL,
            render: (data) => {
                const ldsuId = get(data, "attributes[0].device_id", "");

                return (
                    <span
                        className={`d-flex ${ldsuId ? "mute" : "text-secondary-red-1"
                            }`}
                    >
                        {!ldsuId && (
                            <ErrorOutline className="mr-2" color="error" />
                        )}
                        <span>{ldsuId ?? "LDSU information not found"}</span>
                    </span>
                );
            },
        },
        {
            key: "color",
            label: "Color",
            render: (data, store) => (
                <ColorPicker
                    defaultValue={get(data, "attributes[0].color")}
                    onChange={(e, value) =>
                        store.updatePanel("attributes[0].color", value)
                    }
                    className="chart-col"
                />
            ),
        },
    ],
    [WidgetUpdateTemplateType.GAUGE_COLORS]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "sensorName",
            label: "Sensor",
            render: SensorName,
        },
        {
            key: "type",
            label: "Type",
            render: (data, store) => (
                <span className="mute">
                    {get(data, "attributes[0].name", "-")}
                </span>
            ),
        },
        {
            key: "gateway",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "ldsu",
            label: LDSU_UUID_LABEL,
            render: (data) => {
                const ldsuId = get(data, "attributes[0].device_id", "");

                return (
                    <span
                        className={`d-flex ${ldsuId ? "mute" : "text-secondary-red-1"
                            }`}
                    >
                        {!ldsuId && (
                            <ErrorOutline className="mr-2" color="error" />
                        )}
                        <span>{ldsuId ?? "LDSU information not found"}</span>
                    </span>
                );
            },
        },
        {
            key: "unit",
            label: "Unit",
            render: (data, store) => {
                const deviceInfo = store.getDeviceInfo(data);
                return (
                    <UnitPicker
                        defaultValue={get(data, "attributes[0].unit")}
                        data={
                            deviceInfo?.units ||
                            deviceInfo?.unit
                                ?.split(",")
                                .map((item: string) => item.trim())
                        }
                        onChange={(e, value) =>
                            store.updatePanel("attributes[0].unit", value)
                        }
                        className="chart-col"
                    />
                );
            },
        },
        {
            key: "range",
            label: "Value Range",
            render: (data, store) => {
                const deviceInfo = store.getDeviceInfo(data);

                return (
                    <span>
                        {deviceInfo.min}-{deviceInfo.max}
                    </span>
                );
            },
        },
        {
            key: "color",
            label: "Range",
            render: (data, store) => {
                const rawColors = data.attributes?.[0]?.color;
                const colors = Array.isArray(rawColors)
                    ? (rawColors as ColorStop[])
                    : buildRangeColors([], 4);
                const defaultMin = 0;
                const defaultMax = 100;
                const range = Math.min(Math.max(colors.length || 4, 2), 4);
                const deviceInfo = store.getDeviceInfo(data);
                return (
                    <ColorRangeEditor
                        colors={colors}
                        range={range}
                        min={deviceInfo.min ?? defaultMin}
                        max={deviceInfo.max ?? defaultMax}
                        onRangeChange={(nextRange) => {
                            store.updatePanel(
                                "attributes[0].color",
                                buildRangeColors(colors, nextRange)
                            );
                        }}
                        onColorChange={(value, index) => {
                            const currentOffset = get(
                                colors,
                                `[${index}][0]`,
                                getRangeOffsets(range)[index]
                            );
                            store.updatePanel(`attributes[0].color[${index}]`, [
                                currentOffset,
                                value,
                            ]);
                        }}
                        onStopsChange={(nextColors) => {
                            store.updatePanel(
                                "attributes[0].color",
                                nextColors
                            );
                        }}
                    />
                );
            },
        },
    ],
    [WidgetUpdateTemplateType.ACTUATOR]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "actuator_name",
            label: "Actuator",
            render: SensorName,
        },
        {
            key: "type",
            label: "Type",
            render: (data, store) => (
                <span className="mute">
                    {startCase(get(data, "attributes[0].name", "-"))}
                </span>
            ),
        },
        {
            key: "gateway",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "ldsu",
            label: LDSU_UUID_LABEL,
            render: (data) => {
                const ldsuId = get(data, "attributes[0].device_id", "");

                return (
                    <span
                        className={`d-flex ${ldsuId ? "mute" : "text-secondary-red-1"
                            }`}
                    >
                        {!ldsuId && (
                            <ErrorOutline className="mr-2" color="error" />
                        )}
                        <span>{ldsuId ?? "LDSU information not found"}</span>
                    </span>
                );
            },
        },
        {
            key: "color",
            label: "Color",
            render: (data, store) => (
                <ColorPicker
                    defaultValue={get(data, "attributes[0].color")}
                    onChange={(e, value) =>
                        store.updatePanel("attributes[0].color", value)
                    }
                    className="chart-col"
                />
            ),
        },
    ],
    [WidgetUpdateTemplateType.GATEWAY]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "gateway_name",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "uuid",
            label: "UUID",
            render: (data) => (
                <span className="mute">
                    {get(data, "attributes[0].gateway_id", "-")}
                </span>
            ),
        },
    ],
    [WidgetUpdateTemplateType.CARD_GATEWAY]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "gateway_name",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "uuid",
            label: "UUID",
            render: (data) => (
                <span className="mute">
                    {get(data, "attributes[0].gateway_id", "-")}
                </span>
            ),
        },
        {
            key: "show_location",
            label: "Show Location",
            render: (data, store) => {
                const gatewayId = get(data, "attributes[0].gateway_id", "");
                const gateway = store.allRegistry.find(
                    ({ gateway_id }: { gateway_id: string }) =>
                        gateway_id === gatewayId
                );
                const hasLocation = !!(
                    (
                        gateway?.location?.length === 2 &&
                        gateway?.location[0] &&
                        gateway?.location[1]
                    ) // [0, 0] is considered as no location
                );

                return (
                    <div className="d-flex align-items-center gap-3">
                        {!hasLocation && (
                            <span
                                style={{
                                    fontSize: 12,
                                    color: "#828FAB",
                                }}
                            >
                                Location information not available
                            </span>
                        )}

                        <div>
                            <Form.Check
                                type="switch"
                                id={`switch-show-location-${gatewayId}`}
                                label=""
                                checked={
                                    hasLocation
                                        ? Boolean(
                                            get(
                                                data,
                                                "attributes[0].show_location",
                                                false
                                            )
                                        )
                                        : false
                                }
                                disabled={!hasLocation}
                                onChange={(event) => {
                                    store.updatePanel(
                                        "attributes[0].show_location",
                                        event.target.checked
                                    );
                                }}
                            />
                        </div>
                    </div>
                );
            },
        },
    ],
    [WidgetUpdateTemplateType.INDIVIDUAL_HEATMAP]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "sensor_name",
            label: "Sensor",
            render: (data, store) => {
                const device = store?.deviceList?.find(
                    (device) =>
                        device.gatewayId === data?.attributes[0]?.gateway_id
                );
                const deviceName = device?.sensorName;

                return (
                    <span
                        className={`d-flex ${deviceName ? "mute" : "text-secondary-red-1"
                            }`}
                    >
                        {!deviceName && (
                            <ErrorOutline className="mr-2" color="error" />
                        )}
                        <span>{deviceName || defaultDevicename}</span>
                    </span>
                );
            },
        },
        {
            key: "type",
            label: "Type",
            render: (data, store) => {
                const device = store?.deviceList?.find(
                    (device) =>
                        device.gatewayId === data?.attributes[0]?.gateway_id
                );

                return (
                    <span className="mute">{device?.sensorType || "-"}</span>
                );
            },
        },
        {
            key: "gateway",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "uuid",
            label: "UUID",
            render: (data) => (
                <span className="mute">
                    {get(data, "attributes[0].gateway_id", "-")}
                </span>
            ),
        },
    ],
    [WidgetUpdateTemplateType.SINGLE_CONSUMPTION]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "sensorName",
            label: "Sensor",
            render: SensorName,
        },
        {
            key: "type",
            label: "Type",
            render: (data, store) => (
                <span className="mute">
                    {get(data, "attributes[0].name", "-")}
                </span>
            ),
        },
        {
            key: "gateway",
            label: GATEWAY_LABEL,
            render: GatewayName,
        },
        {
            key: "ldsu",
            label: LDSU_UUID_LABEL,
            render: (data) => {
                const ldsuId = get(data, "attributes[0].device_id", "");

                return (
                    <span
                        className={`d-flex ${ldsuId ? "mute" : "text-secondary-red-1"
                            }`}
                    >
                        {!ldsuId && (
                            <span className="material-icons mr-2">
                                error_outline
                            </span>
                        )}
                        <span>{ldsuId ?? "LDSU information not found"}</span>
                    </span>
                );
            },
        },
        {
            key: "color",
            label: "Color",
            render: (data, store) => (
                <ColorPicker
                    defaultValue={get(data, "attributes[0].color")}
                    onChange={(e, value) =>
                        store.updatePanel("attributes[0].color", value)
                    }
                    className="chart-col"
                />
            ),
        },
    ],
    [WidgetUpdateTemplateType.EVENT]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
        {
            key: "color",
            label: "Colors",
            render: (data, store) => {
                const colors = data.attributes?.[0]?.color ?? [];
                return colors.map((color: string, index: number) => (
                    <div
                        key={`event-color-slot-${get(color, 0)}`}
                        className="mr-2"
                    >
                        <ColorPicker
                            aria-label="color-picker"
                            defaultValue={color?.[1]}
                            onChange={(e, value) => {
                                const updateColor = [index, value];
                                store.updatePanel(
                                    `attributes[0].color[${index}]`,
                                    updateColor
                                );
                            }}
                        />
                    </div>
                ));
            },
        },
    ],
    [WidgetUpdateTemplateType.SUBSCRIPTION]: [
        {
            key: "name",
            label: "Widget Name",
            render: UpdateSingleChartTemplateRenderForName,
        },
    ],
    [WidgetUpdateTemplateType.PSL]: createPSLTemplate(),
};
